import { RiskKeys } from '@/config';
import { AlertStatusType, AlertType, Numeric } from '@/models';

enum ComplianceSort {
  Asc = 'ASC',
  Desc = 'DESC',
}

enum ComplianceAlertOrder {
  Id = 'id',
  Level = 'level',
  Status = 'status',
  Risky = 'risky',
}

enum ComplianceTxOrder {
  Id = 'id',
  Level = 'level',
  Amount = 'amount',
}

enum ComplianceCustomerOrder {
  Id = 'id',
  Level = 'level',
  Count = 'totalCount',
  Amount = 'totalAmount',
  Risky = 'totalRisky',
  CountAlerts = 'totalAlerts',
  NewAlerts = 'newAlerts',
}

enum CompRiskLevel {
  Low = 'low',
  Medium = 'medium',
  High = 'high',
  Severe = 'severe',
  NA = 'na',
}

enum AlertRuleType {
  TX_COUNT = 'txCount',
  COMMON = 'common',
}

type Direction = 'income' | 'outcome';
type CompAlertStatus = AlertStatusType;

type InteractionType = 'direct' | 'indirect';

type TransactionAlertsLevels = {
  [key in RiskKeys]?: {
    [direction in Direction]?: {
      [interactionType in InteractionType]?: {
        low: number | null;
        medium: number | null;
        high: number | null;
        severe: number | null;
        na: number | null;
      };
    };
  };
};

type TransactionAlerts = {
  categories: RiskKeys[];
  levels: TransactionAlertsLevels;
  directions: Direction[];
  interactionTypes: InteractionType[];
};

type CompCategory = {
  id: number;
  name: string;
};

type GetAlertLevelsSuccessResponse = {
  data: TransactionAlerts;
  result: boolean;
};

type AlertListSearch = {
  cid?: string;
  status?: CompAlertStatus;
};

type AlertListOrder = {
  field?: ComplianceAlertOrder;
  sort?: ComplianceSort;
};

type AlertListRequest = AlertListSearch &
  AlertListOrder & {
    limit: number;
    offset: number;
  };

type CompCustomerBrief = {
  id: string;
  name: string;
  level: string; // TODO: Update to actual type
  identifier?: string;
};

type CompCounterparty = {
  id?: number;
  name?: string;
  type?: string;
  slug?: string;
  address?: string;
};

type BehaviorTx = {
  id: string;
  createdDate: string;
  updatedDate?: string;
  confirmedDate?: string;
  nextUpdateTs?: string;
  blockchain: string;
  asset?: string | null;
  hash: string;
  direction: Direction;
  address: string;
  flow?: string;
  memo?: string;
  level: CompRiskLevel;
};

type AlertRuleParams = {
  count: number;
  intervalType: AlertRuleIntervalType;
  intervalValue: number;
  minAmount?: number;
  maxAmount?: number;
  isBlacklisted?: boolean;
};

type AlertRuleBriefCategory = {
  id: string;
  name: string;
  isActive?: boolean;
};

type AlertRuleBrief = {
  id: string;
  name: string;
  type: AlertRuleType;
  isActive: boolean;
  risk: CompRiskLevel;
  direction: AlertRuleDirection;
  params: AlertRuleParams;
  categories?: AlertRuleBriefCategory[];
  categoryJoinCondition?: AlertRuleCategoryJoin;
};

type AlertStatsListItem = {
  id: string;
  uid: string;
  customer: CompCustomerBrief[];
  direction: Direction;
  address: string;
  causingTxHash: string;
  counterparty: CompCounterparty;
  risk: CompRiskLevel;
  status: CompAlertStatus;
  risky: number;
  createdDate: string;
  categories?: (CompCategory | string)[];
  behaviorTxs?: BehaviorTx[];
  rule?: AlertRuleBrief;
};

type AlertListTableItem = {
  key: React.Key;
  id: string;
  uid: string;
  user: string;
  address: string;
  causingTxHash: string;
  direction: Direction;
  risk: AlertType;
  riskyFunds: number;
  entityCategory: RiskKeys[] | null;
  state: CompAlertStatus;
  date: string;
  customer: CompCustomerBrief[];
  behaviorTxs?: BehaviorTx[];
  rule?: AlertRuleBrief;
};

type ComplianceAlertRow = {
  key: React.Key;
  customer: string;
  alertRisk: AlertType;
  alertStatus: CompAlertStatus;
  fundsAmount: number;
};

type AlertListResponse = {
  data: {
    items: AlertStatsListItem[];
    total: number;
  };
  result: boolean;
};

type LevelData = {
  [key in CompRiskLevel]?: number | null;
};

type TransactionAlert = {
  category: RiskKeys;
  direction: Direction;
  interactionType: InteractionType;
  levels: LevelData;
};

type UpdateTransactionAlertRequest = {
  [category in RiskKeys]?: {
    [direction in Direction]?: {
      [interactionType in InteractionType]?: {
        [alertGrade in CompRiskLevel]?: LevelData;
      };
    };
  };
};

type UpdateTransactionAlertResponse = {
  data: TransactionAlerts;
  result: boolean;
};

type UpdateAlertStatusRequest = {
  id: string;
  status: CompAlertStatus;
};

type UpdateAlertStatusResponse = {
  result: boolean;
  data: boolean;
};

type CustomerStatsByLevel<T = number | null> = {
  [clKey in CompRiskLevel]?: T;
};

type StatsResponse = {
  customers: {
    byLevel: CustomerStatsByLevel<number>;
  };
  transactions: {
    total: number;
    income: number;
    outcome: number;
  };
  alerts: {
    byRisk: {
      [arKey in CompRiskLevel]: number;
    };
    byStatus: {
      [asKey in CompAlertStatus]: number;
    };
  };
};

type TxListSearch = {
  cid?: string;
  status?: CompAlertStatus;
};

type TxListOrder = {
  field?: ComplianceTxOrder;
  sort?: ComplianceSort;
};

type TxListRequest = TxListSearch &
  TxListOrder & {
    limit: number;
    offset: number;
  };

type TxListItem = {
  id: Numeric;
  uid: string;
  customer: CompCustomerBrief[];
  hash: string;
  address: string;
  counterparty: CompCounterparty;
  direction: Direction;
  level: CompRiskLevel;
  amount: number;
  nextUpdateTs: string;
};

type TxListResponse = {
  data: {
    items: TxListItem[];
    total: number;
  };
  result: boolean;
};

type TxRecheckRequest = {
  id: Numeric;
};

type TxResponse = {
  data: TxListItem;
  result: boolean;
};

type ComplianceTransferRow = {
  key: React.Key;
  uid: string;
  customer: CompCustomerBrief[];
  user: string;
  txHash: string;
  address: string;
  counterparty: CompCounterparty;
  direction: Direction;
  level: CompRiskLevel;
  fundsTransferred: number;
  nextUpdate: string;
};

type CustomerListSearch = {
  cid?: string;
  level?: CompRiskLevel;
};

type CustomerListOrder = {
  field?: ComplianceCustomerOrder;
  sort?: ComplianceSort;
};

type ComplianceCustomerRequest = CustomerListSearch &
  CustomerListOrder & {
    limit: number;
    offset: number;
  };

type CustomerListItemExtras = {
  isSuspended?: boolean | null;
  country?: {
    id: string;
    name: string;
    code: ListsCountriesCodes;
  };
  lastActivity?: string | null;
};

type CustomerListItem = {
  id: Numeric;
  name: string;
  identifier?: string;
  level: CompRiskLevel;
  extras?: CustomerListItemExtras;
  totalCount: number;
  totalAmount: number;
  totalRisky: number;
  totalAlerts: number;
  newAlerts: number;
};

type CustomerListTableItem = {
  key: React.Key;
  user: string;
  topAddress?: string;
  risk?: AlertType;
  entity?: RiskKeys;
  totalTransfers: number;
  totalFunds: number;
  riskyFunds: number;
  totalAlerts: number;
  newAlerts?: number;
  country?: ListsCountriesCodes;
  isSuspended?: boolean;
  lastActivity?: string;
};

type ComplianceCustomerResponse = {
  success?: boolean;
  data: {
    items: CustomerListItem[];
    total: number;
  };
  result: boolean;
};

type UpdateCustomerRequest = {
  id?: string;
  name?: string;
  isSuspended?: boolean;
  country?: ListsCountriesCodes | null;
};

type TopCustomersResponse = {
  name: string | null;
  income: number;
  outcome: number;
  total: number;
  expTotal: number | null;
  expIncome: number | null;
  expOutcome: number | null;
}[];

type CustomerListResponse = CustomerListItem[];

type CustomerStatsData = {
  byLevel: {
    [key in AlertType]: number;
  };
  totals?: {
    byLevel: number;
  };
};

type CustomerStatsResponse = {
  data: CustomerStatsData;
  result: boolean;
};

type CustomerCategoryDataEntry = {
  name: RiskKeys;
  income: number;
  outcome: number;
  total: number;
  expTotal: number;
  expIncome: number;
  expOutcome: number;
};

type CustomerCategoryData = Record<string, CustomerCategoryDataEntry>;

type CustomerCategoryStatsResponse = {
  data: CustomerCategoryData;
  result: boolean;
};

type CategoryStatRow = {
  key: React.Key;
  entityCategory: RiskKeys;
  totalFunds: number;
  receivedFunds: number;
  sentFunds: number;
  totalExpenses: number;
  receivedExpenses: number;
  sentExpenses: number;
};

type AlertStats = {
  byRisk: {
    [key in AlertType]: number;
  };
  byStatus: {
    [key in CompAlertStatus]: number;
  };
  totalByRisk?: number;
  totalByStatus?: number;
};

type AlertStatsResponse = {
  data: AlertStats;
  result: boolean;
};

type TransactionBriefStats = {
  count: number;
};

type TransactionBriefStatsResponse = {
  data: TransactionBriefStats;
  result: boolean;
};

type TransactionStats = TransactionBriefStats & {
  total: number;
  income: number;
  outcome: number;
};

type TransactionStatsResponse = {
  data: TransactionStats;
  result: boolean;
};

type ResetTransactionAlertsCategoriesRequest = {
  categories: RiskKeys[];
  direction?: Direction;
};

type AlertRuleData = {
  items: AlertRule[];
  total: number;
};

type AlertRuleIntervalType = 'minute' | 'hour' | 'day';

type AlertRuleCategoryJoin = 'all' | 'any';

type AlertRuleTxCountParams = {
  count: number;
  intervalType: AlertRuleIntervalType;
  intervalValue: number;
  minAmount?: number;
  maxAmount?: number;
};

type AlertRuleCommonParams = {
  minVolume?: number;
  countries?: ListsCountriesCodes[];
  isSuspended?: boolean;
  isBlacklisted?: boolean;
  targetCustomerRisk?: CompRiskLevel;
  inactivityType?: AlertRuleIntervalType;
  inactivityValue?: number;
  intervalType?: AlertRuleIntervalType;
  intervalValue?: number;
};

type AlertRule = {
  id?: number | string;
  name: string;
  type: AlertRuleType;
  risk: CompRiskLevel;
  direction: AlertRuleDirection;
  categories?: RiskKeys[];
  categoryJoin?: AlertRuleCategoryJoin;
  categoryJoinCondition?: AlertRuleCategoryJoin;
  params: AlertRuleTxCountParams | AlertRuleCommonParams;
  isActive?: boolean;
};

type CreateAlertRuleRequest = Omit<AlertRule, 'id' | 'isActive'>;

type CreateAlertRuleResponse = {
  data: AlertRule;
  result: boolean;
};

type AlertRuleDirection = Direction | 'all';

type ListAlertRulesRequest = {
  limit: number;
  offset: number;
  sort: string;
  order: string;
  filter: {
    name?: string;
    type?: AlertRuleType;
    risk?: CompRiskLevel;
    direction?: AlertRuleDirection;
    categories?: RiskKeys[];
    active?: boolean;
  };
};

type ListAlertRulesResponse = {
  data: AlertRuleData;
  result: boolean;
};

type UpdateAlertRuleRequest = Omit<AlertRule, 'isActive'>;

type UpdateAlertRuleResponse = {
  data: AlertRule;
  result: boolean;
};

type DeleteAlertRuleRequest = {
  id: number | string;
};

type DeleteAlertRuleResponse = {
  result: boolean;
};

type ActivateAlertRuleRequest = {
  id: number | string;
};

type ActivateAlertRuleResponse = {
  data: AlertRule;
  result: boolean;
};

type DeactivateAlertRuleRequest = {
  id: number | string;
};

type DeactivateAlertRuleResponse = {
  data: AlertRule;
  result: boolean;
};

export type ListsCountriesCodes =
  | 'ad'
  | 'tt'
  | 'rs'
  | 'gb'
  | 'ru'
  | 'hk'
  | 'eh'
  | 'uy'
  | 'bb'
  | 'ps'
  | 'mx'
  | 'mw'
  | 'aq'
  | 'nc'
  | 'sj'
  | 'nf'
  | 'cx'
  | 'mn'
  | 'lv'
  | 'bn'
  | 'ua'
  | 'kp'
  | 'fi'
  | 'ai'
  | 'az'
  | 'ky'
  | 'uz'
  | 'bz'
  | 'ht'
  | 'co'
  | 'cr'
  | 'do'
  | 'sv'
  | 'gt'
  | 'sy'
  | 'tm';

export type ListsCountries = Record<ListsCountriesCodes, string>;

export type ListsData = {
  directions?: string[];
  riskLevels?: string[];
  categories?: string[];
  alertStatuses?: string[];
  checkStatuses?: string[];
  countries?: ListsCountries;
};

type ListsResponse = ListsData;

export type {
  Direction,
  CompAlertStatus,
  GetAlertLevelsSuccessResponse,
  AlertListSearch,
  AlertListOrder,
  AlertListRequest,
  CompCustomerBrief,
  CompCounterparty,
  BehaviorTx,
  AlertRuleParams,
  AlertRuleBrief,
  AlertStatsListItem,
  AlertListTableItem,
  AlertListResponse,
  LevelData,
  TransactionAlert,
  UpdateTransactionAlertRequest,
  UpdateTransactionAlertResponse,
  UpdateAlertStatusRequest,
  UpdateAlertStatusResponse,
  CustomerStatsByLevel,
  StatsResponse,
  TxListSearch,
  TxListOrder,
  TxListRequest,
  TxListItem,
  TxListResponse,
  CustomerListSearch,
  CustomerListOrder,
  ComplianceCustomerRequest,
  ComplianceCustomerResponse,
  UpdateCustomerRequest,
  CustomerListItem,
  TopCustomersResponse,
  CustomerListResponse,
  CustomerStatsData,
  CustomerStatsResponse,
  CustomerCategoryData,
  CustomerCategoryStatsResponse,
  AlertStats,
  AlertStatsResponse,
  TransactionBriefStatsResponse,
  TransactionStatsResponse,
  CategoryStatRow,
  TransactionStats,
  ComplianceTransferRow,
  ComplianceAlertRow,
  CustomerListTableItem,
  TransactionAlertsLevels,
  TransactionAlerts,
  TxResponse,
  TxRecheckRequest,
  InteractionType,
  ResetTransactionAlertsCategoriesRequest,
  AlertRuleDirection,
  AlertRuleIntervalType,
  AlertRuleData,
  AlertRule,
  CreateAlertRuleRequest,
  CreateAlertRuleResponse,
  ListAlertRulesRequest,
  ListAlertRulesResponse,
  UpdateAlertRuleRequest,
  UpdateAlertRuleResponse,
  DeleteAlertRuleRequest,
  DeleteAlertRuleResponse,
  ActivateAlertRuleRequest,
  ActivateAlertRuleResponse,
  DeactivateAlertRuleRequest,
  DeactivateAlertRuleResponse,
  ListsResponse,
  AlertRuleTxCountParams,
  AlertRuleCommonParams,
  AlertRuleCategoryJoin,
};

export {
  ComplianceSort,
  ComplianceAlertOrder,
  ComplianceTxOrder,
  ComplianceCustomerOrder,
  CompRiskLevel,
  AlertRuleType,
};
